import { useState, type ClipboardEvent, type Dispatch, type SetStateAction } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { webColors } from '@liminal/brand/semantic';
import { Button } from '@liminal/web-ui/primitives/Button';
import { IconButton } from '@liminal/web-ui/primitives/IconButton';
import { Link } from '@liminal/web-ui/primitives/Link';
import { Input } from '@liminal/web-ui/forms/Input';
import { SegmentedToggle, type SegmentedOption } from '@liminal/web-ui/forms/SegmentedToggle';
import { FieldError } from '@liminal/web-ui/feedback/FieldError';
import { WarningCard } from '@liminal/web-ui/feedback/WarningCard';
import { getErrorMessage } from '@/errors/messages';
import { useResolveLocationLink } from '@/hooks/useRewards';
import { isGoogleMapsLink, mapsSearchUrl, parseCoordinates, parseDegrees } from '@/lib/rewards';
import {
  ADDRESS_MAX,
  GOOGLE_MAPS_LINK_LABEL,
  LINK_URI_MAX,
  LOCATION_LABEL_MAX,
  MAX_LINKS,
  MAX_LOCATIONS,
  newLinkLocation,
  newRowKey,
  resolvedPlace,
  type CatalogueErrors,
  type CatalogueForm,
  type LinkEntry,
  type LocationEntry,
  type LocationRow,
  type ResolvedPlace,
} from '../catalogueForm';

/** The two ways into a row (LIM-4621). A new row starts on its link. */
const ENTRY_MODES: readonly SegmentedOption<LocationEntry['mode']>[] = [
  { value: 'link', label: 'Google Maps link' },
  { value: 'address', label: 'Address and coordinates' },
];

/** Said of a link left in the field that the resolver does not read, so it is never sent. */
const NOT_A_MAPS_LINK = 'This is not a Google Maps link.';

/** Whether a row is still waiting on the answer for `uri`: not removed, switched to address, or given another link since. */
function awaiting(row: LocationRow, uri: string): row is LocationRow & { readonly entry: LinkEntry } {
  return row.entry.mode === 'link' && row.entry.resolution.state === 'resolving' && row.entry.resolution.uri === uri;
}

interface LocationsEditorProps {
  readonly form: CatalogueForm;
  readonly setForm: Dispatch<SetStateAction<CatalogueForm>>;
  readonly errors: CatalogueErrors['locations'];
}

/**
 * The places customers can visit the partner, one row each, entered either
 * way. By its Google Maps link: the link is pasted, the resolver reads the
 * place, and the row shows its name (editable) and coordinates. By address and
 * coordinates: typed by hand, where a pasted "lat, lng" fills both. Rows read
 * their links independently, so five links pasted into five rows are five
 * requests in flight at once. Each row with a pin links to it on Google Maps,
 * so it can be checked. The first location's link also joins Links, as the
 * partner's Google Maps link, when the partner has none yet.
 */
export function LocationsEditor({ form, setForm, errors }: LocationsEditorProps) {
  const resolver = useResolveLocationLink();
  // The row "Add location" just appended, whose link field takes the focus so the next paste lands in it.
  const [addedKey, setAddedKey] = useState<string | null>(null);
  const full = form.locations.length >= MAX_LOCATIONS;

  const updateLocation = (key: string, update: (row: LocationRow) => LocationRow) =>
    setForm((f) => ({ ...f, locations: f.locations.map((row) => (row.key === key ? update(row) : row)) }));

  const setLocation = (key: string, patch: Partial<Omit<LocationRow, 'key' | 'entry'>>) =>
    updateLocation(key, (row) => ({ ...row, ...patch }));

  const addLocation = () => {
    const key = newRowKey();
    setForm((f) => ({ ...f, locations: [...f.locations, newLinkLocation(key)] }));
    setAddedKey(key);
  };

  const removeLocation = (key: string) => setForm((f) => ({ ...f, locations: f.locations.filter((row) => row.key !== key) }));

  // The label, coordinates and address stay as they are either way, so a
  // resolved row switched to address shows them ready to adjust. A row
  // switched to its link starts with an empty one.
  const setMode = (key: string, mode: LocationEntry['mode']) =>
    updateLocation(key, (row) => ({
      ...row,
      entry: mode === 'address' ? { mode: 'address' } : { mode: 'link', uri: '', resolution: { state: 'unsent' } },
    }));

  // Every write goes through the row's own key and checks the row is still
  // waiting on this link, so concurrent reads never touch each other's rows,
  // and an answer that comes back after the row moved on is dropped.
  const readLink = async (key: string, uri: string) => {
    updateLocation(key, (row) => ({ ...row, entry: { mode: 'link', uri, resolution: { state: 'resolving', uri } } }));
    let place: ResolvedPlace;
    try {
      place = resolvedPlace(await resolver.mutateAsync(uri), '');
    } catch (e) {
      const message = getErrorMessage(e);
      updateLocation(key, (row) =>
        awaiting(row, uri) ? { ...row, entry: { ...row.entry, resolution: { state: 'failed', uri, message } } } : row,
      );
      return;
    }
    setForm((f) => {
      const first = f.locations[0];
      if (!first) return f;
      // The partner's first location, read from its Maps link, also gives the
      // partner its Google Maps link, unless it has one or its links are full.
      const addsLink =
        first.key === key &&
        awaiting(first, uri) &&
        f.links.length < MAX_LINKS &&
        !f.links.some((link) => isGoogleMapsLink(link.uri.trim()));
      return {
        ...f,
        links: addsLink ? [...f.links, { key: newRowKey(), label: GOOGLE_MAPS_LINK_LABEL, uri }] : f.links,
        locations: f.locations.map((row) =>
          row.key === key && awaiting(row, uri)
            ? {
                ...row,
                // The place's own name, for the operator to edit; a link that names no place keeps the row's.
                label: place.label === '' ? row.label : place.label,
                latitude: String(place.coordinates.latitude),
                longitude: String(place.coordinates.longitude),
                entry: { ...row.entry, resolution: { state: 'resolved', uri } },
              }
            : row,
        ),
      };
    });
  };

  // A Maps link pasted replaces what the field held and is read at once.
  // Anything else lands in the field as usual.
  const onLinkPaste = (key: string, event: ClipboardEvent<HTMLElement>) => {
    const uri = event.clipboardData.getData('text').trim();
    if (!isGoogleMapsLink(uri)) return;
    event.preventDefault();
    void readLink(key, uri);
  };

  // Leaving the field reads a link typed rather than pasted. One already sent
  // (the paste just before, most often) is not sent again.
  const onLinkBlur = (key: string, entry: LinkEntry) => {
    const uri = entry.uri.trim();
    if (uri === '' || (entry.resolution.state !== 'unsent' && entry.resolution.uri === uri)) return;
    if (isGoogleMapsLink(uri)) {
      void readLink(key, uri);
      return;
    }
    updateLocation(key, (row) =>
      row.entry.mode === 'link'
        ? { ...row, entry: { ...row.entry, resolution: { state: 'failed', uri, message: NOT_A_MAPS_LINK } } }
        : row,
    );
  };

  // "lat, lng" fills both coordinates. A Maps link is what link mode is for,
  // so the row becomes a link row and reads it. Anything else lands in the field.
  const onCoordinatePaste = (key: string, event: ClipboardEvent<HTMLElement>) => {
    const text = event.clipboardData.getData('text').trim();
    const pair = parseCoordinates(text);
    if (pair) {
      event.preventDefault();
      setLocation(key, { latitude: String(pair.latitude), longitude: String(pair.longitude) });
    } else if (isGoogleMapsLink(text)) {
      event.preventDefault();
      void readLink(key, text);
    }
  };

  return (
    <Stack spacing={2}>
      {form.locations.map((row, index) => {
        const rowErrors = errors[row.key];
        const latitude = parseDegrees(row.latitude, 'latitude');
        const longitude = parseDegrees(row.longitude, 'longitude');
        const pin = latitude !== null && longitude !== null && (latitude !== 0 || longitude !== 0) ? { latitude, longitude } : null;
        const { entry } = row;
        return (
          <Stack
            key={row.key}
            spacing={1}
            sx={index === 0 ? undefined : { pt: 2, borderTop: `1px solid ${webColors.border.light}` }}
            data-testid="location-row"
          >
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Box sx={{ flex: 1, minWidth: 0, maxWidth: 480 }}>
                <SegmentedToggle
                  aria-label="How this location is entered"
                  size="sm"
                  value={entry.mode}
                  onChange={(mode) => setMode(row.key, mode)}
                  options={ENTRY_MODES}
                />
              </Box>
              <IconButton icon="close" label="Remove location" size="sm" tone="muted" onClick={() => removeLocation(row.key)} />
            </Stack>

            {entry.mode === 'link' ? (
              <>
                {/* The paste is caught here, around the field: the shared Input takes no onPaste. */}
                <Box onPaste={(event) => onLinkPaste(row.key, event)}>
                  <Input
                    aria-label="Google Maps link"
                    type="url"
                    value={entry.uri}
                    onChange={(uri) =>
                      updateLocation(row.key, (current) =>
                        current.entry.mode === 'link' ? { ...current, entry: { ...current.entry, uri } } : current,
                      )
                    }
                    onBlur={() => onLinkBlur(row.key, entry)}
                    placeholder="https://maps.app.goo.gl/…"
                    maxLength={LINK_URI_MAX}
                    autoFocus={row.key === addedKey}
                    error={rowErrors?.uri}
                  />
                </Box>
                {entry.resolution.state === 'resolving' && (
                  <WarningCard tone="info" compact>
                    Reading the place from this link…
                  </WarningCard>
                )}
                {entry.resolution.state === 'failed' && (
                  <WarningCard
                    tone="error"
                    compact
                    action={{ label: 'Enter address and coordinates', onClick: () => setMode(row.key, 'address') }}
                  >
                    {entry.resolution.message}
                  </WarningCard>
                )}
                {entry.resolution.state === 'resolved' && (
                  <>
                    <Input
                      aria-label="Location name"
                      value={row.label}
                      onChange={(label) => setLocation(row.key, { label })}
                      placeholder="Name, such as Pinheiros"
                      maxLength={LOCATION_LABEL_MAX}
                      error={rowErrors?.label}
                    />
                    {/* Read-only: the pin is the link's. Address and coordinates is where it is adjusted. */}
                    <Typography variant="body2" color="text.secondary" data-testid="location-pin">
                      {[`${row.latitude}, ${row.longitude}`, row.address.trim()].filter((part) => part !== '').join(' · ')}
                      {pin && (
                        <>
                          {' · '}
                          <Link href={mapsSearchUrl(pin)} external>
                            Open in Maps
                          </Link>
                        </>
                      )}
                    </Typography>
                    <FieldError error={rowErrors?.latitude ?? rowErrors?.longitude} reserveSpace={false} />
                  </>
                )}
              </>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)' },
                    gap: 2,
                    alignItems: 'start',
                  }}
                >
                  <Input
                    aria-label="Location name"
                    value={row.label}
                    onChange={(label) => setLocation(row.key, { label })}
                    placeholder="Name, such as Pinheiros"
                    maxLength={LOCATION_LABEL_MAX}
                    error={rowErrors?.label}
                  />
                  <Box onPaste={(event) => onCoordinatePaste(row.key, event)}>
                    <Input
                      aria-label="Latitude"
                      value={row.latitude}
                      onChange={(value) => setLocation(row.key, { latitude: value })}
                      placeholder="Latitude"
                      inputMode="decimal"
                      error={rowErrors?.latitude}
                    />
                  </Box>
                  <Box onPaste={(event) => onCoordinatePaste(row.key, event)}>
                    <Input
                      aria-label="Longitude"
                      value={row.longitude}
                      onChange={(value) => setLocation(row.key, { longitude: value })}
                      placeholder="Longitude"
                      inputMode="decimal"
                      error={rowErrors?.longitude}
                    />
                  </Box>
                </Box>
                <Input
                  aria-label="Address"
                  value={row.address}
                  onChange={(address) => setLocation(row.key, { address })}
                  placeholder="Address, optional"
                  maxLength={ADDRESS_MAX}
                  error={rowErrors?.address}
                />
                {pin && (
                  <Typography variant="body2">
                    <Link href={mapsSearchUrl(pin)} external>
                      Open in Maps
                    </Link>
                  </Typography>
                )}
              </>
            )}
          </Stack>
        );
      })}
      <Stack direction="row">
        <Button variant="link" size="sm" startIcon="plus" disabled={full} onClick={addLocation}>
          Add location
        </Button>
      </Stack>
      {full && (
        <Typography variant="caption" color="text.secondary">
          {`A partner can have at most ${MAX_LOCATIONS} locations.`}
        </Typography>
      )}
    </Stack>
  );
}
